import { useCallback, useEffect, useRef, useState } from "react";
import { cacheImages, imageUrls, initDevice, loadLocal, refreshCustomers, refreshFromServer, retryRequestedReceipts, syncPending } from "../components/pos/store";
import api from "../lib/api";
import PosHeader from "../components/pos/PosHeader";
import Register from "../components/pos/Register";
import Sales from "../components/pos/Sales";
import Customers from "../components/pos/Customers";
import Setup from "../components/pos/Setup";

const EMPTY = { event: null, items: [], customers: [], txns: [], prepared: false };

export default function Pos() {
    const [tab, setTab] = useState("pos");
    const [data, setData] = useState(EMPTY);
    const [imgs, setImgs] = useState({});
    const [online, setOnline] = useState(navigator.onLine);
    const [loaded, setLoaded] = useState(false);
    const [shell, setShell] = useState(false);
    const syncing = useRef(false);
    const waReady = useRef(false);

    const reload = useCallback(async () => {
        const d = await loadLocal();
        setData(d);
        setImgs(await imageUrls(d.event, d.items));
        setLoaded(true);
        return d;
    }, []);

    const prepare = useCallback(async () => {
        try {
            await refreshFromServer();
            setOnline(true);
            api.get("/pos/whatsapp/status", { timeout: 8000 }).then(({ data }) => { waReady.current = !!data.configured; }).catch(() => {});
        } catch (e) {
            if (!e.response) setOnline(false);
        }
        const d = await reload();
        if (d.event?.status === "ACTIVE" && !d.prepared) {
            await cacheImages(d.event, d.items).catch(() => {});
            await reload();
        }
    }, [reload]);

    const runSync = useCallback(async () => {
        if (syncing.current) return;
        syncing.current = true;
        try {
            const r = await syncPending();
            if (waReady.current) await retryRequestedReceipts();
            if (r.attempted) {
                setOnline(true);
                await refreshCustomers().catch(() => {});
                await reload();
            }
        } catch (e) {
            if (!e.response) setOnline(false);
            await reload();
        } finally {
            syncing.current = false;
        }
        const d = await loadLocal();
        if (navigator.onLine && d.event?.status === "ACTIVE" && !d.prepared) {
            await cacheImages(d.event, d.items).catch(() => {});
            await reload();
        }
    }, [reload]);

    useEffect(() => {
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.register("/pos-sw.js", { scope: "/pos" }).then(() => navigator.serviceWorker.ready).then(() => setShell(true)).catch(() => {});
        }
        initDevice().then(reload).then(prepare).then(runSync);
        const up = () => { setOnline(true); runSync(); };
        const down = () => setOnline(false);
        const vis = () => { if (document.visibilityState === "visible") runSync(); };
        window.addEventListener("online", up);
        window.addEventListener("offline", down);
        document.addEventListener("visibilitychange", vis);
        const t = setInterval(runSync, 20000);
        return () => {
            window.removeEventListener("online", up);
            window.removeEventListener("offline", down);
            document.removeEventListener("visibilitychange", vis);
            clearInterval(t);
        };
    }, [reload, prepare, runSync]);

    const afterSale = useCallback(async () => {
        await reload();
        setTimeout(runSync, 0);
    }, [reload, runSync]);

    const eventTxns = data.event ? data.txns.filter((t) => t.event_id === data.event.id) : [];
    return (
        <div className="space-y-4" data-testid="pos-page">
            <PosHeader event={data.event} online={online} prepared={data.prepared && shell} tab={tab} setTab={setTab}
                pending={data.txns.filter((t) => t.sync_status === "SAVED" || t.sync_status === "PENDING").length} />
            {!loaded ? <p className="text-sm text-neutral-400">Loading POS…</p> : (
                <>
                    {tab === "pos" && <Register data={data} imgs={imgs} onSale={afterSale} reload={reload} online={online} goSetup={() => setTab("setup")} />}
                    {tab === "sales" && <Sales event={data.event} txns={eventTxns} online={online} reload={reload} />}
                    {tab === "customers" && <Customers customers={data.customers} txns={data.txns} online={online} reload={reload} />}
                    {tab === "setup" && <Setup localEvent={data.event} online={online} onChanged={prepare} pending={data.txns.filter((t) => t.sync_status !== "SYNCED").length} />}
                </>
            )}
        </div>
    );
}
